// The patient currently open in the session workspace (SCRUM-851). The My
// Tasks RPM timer runs only while this is set, so it starts when a reviewer
// lands on a patient - not on the selection / prompt / All Done screens -
// and stops (saving its time) when they leave the workspace.
export const OPEN_PATIENT_KEY = "reading-review/open-patient";
const OPEN_SESSION_KEY = "reading-review/open-session";

export const setOpenPatient = (patientId, sessionId) => {
  sessionStorage.setItem(OPEN_PATIENT_KEY, patientId);
  if (sessionId != null) sessionStorage.setItem(OPEN_SESSION_KEY, sessionId);
};

export const clearOpenPatient = () => {
  sessionStorage.removeItem(OPEN_PATIENT_KEY);
  sessionStorage.removeItem(OPEN_SESSION_KEY);
};

export const getOpenPatient = () => sessionStorage.getItem(OPEN_PATIENT_KEY);

// SCRUM-850: the timer's count on a patient while the review session is
// paused (Pause Session, Back to Selection, idle, or another browser tab
// taking over), so continuing that session on that patient - in this tab
// or a duplicated one - starts from it (e.g. 02:10) instead of 00:00. The
// time itself is saved when the timer pauses; this only carries the count
// shown. In localStorage so every tab sees it.
const HELD_TIMER_KEY = "reading-review/held-timer";

const readHeldTimer = () => {
  try {
    return JSON.parse(localStorage.getItem(HELD_TIMER_KEY) || "null");
  } catch {
    return null;
  }
};

// The held count for this patient in the session open now (the timer is
// created after setOpenPatient, so it's this tab's session).
export const heldTimerFor = (patientId) => {
  const sessionId = sessionStorage.getItem(OPEN_SESSION_KEY);
  const isThis = (held) =>
    !!held && String(held.sessionId) === String(sessionId) && String(held.patientId) === String(patientId);
  return {
    get: () => {
      const held = readHeldTimer();
      return isThis(held) ? held.seconds || 0 : 0;
    },
    hold: (seconds) =>
      localStorage.setItem(HELD_TIMER_KEY, JSON.stringify({ sessionId, patientId: String(patientId), seconds })),
    release: () => {
      if (isThis(readHeldTimer())) localStorage.removeItem(HELD_TIMER_KEY);
    },
  };
};

// SCRUM-850: a note saved in one browser tab records the patient's time, so
// another tab paused on the same patient has nothing left to resume.
const TIME_SAVED_KEY = "reading-review/time-saved";

export const announceTimeSaved = (sessionId, patientId) =>
  localStorage.setItem(
    TIME_SAVED_KEY,
    JSON.stringify({ sessionId: String(sessionId), patientId: String(patientId), at: Date.now() })
  );

// Calls handler({ sessionId, patientId }) when another tab announces a save.
export const onTimeSavedElsewhere = (handler) => {
  const listener = (event) => {
    if (event.key !== TIME_SAVED_KEY || !event.newValue) return;
    try {
      handler(JSON.parse(event.newValue));
    } catch {
      // ignore a malformed value
    }
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
};

// SCRUM-850: pauses / resumes the My Tasks RPM timer with the review session
// (Pause Session, idle or tab-left pauses, admin end, Resume Session). A
// pause saves the time recorded so far; rewindSeconds takes idle time back
// off first.
export const REVIEW_TIMER_EVENT = "reading-review/timer";

export const signalReviewTimer = (action, { rewindSeconds = 0 } = {}) =>
  window.dispatchEvent(new CustomEvent(REVIEW_TIMER_EVENT, { detail: { action, rewindSeconds } }));
